import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { siteContent } from '../data/mock';
import { Heart, Phone, MapPin } from 'lucide-react';

const OmLogo = ({ className }) => (
  <svg viewBox="0 0 100 100" className={className}>
    <defs>
      <linearGradient id="footerOmGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" style={{ stopColor: '#F59E0B' }} />
        <stop offset="100%" style={{ stopColor: '#F97316' }} />
      </linearGradient>
    </defs>
    <circle cx="50" cy="50" r="48" fill="url(#footerOmGradient)" />
    <text x="50" y="68" fontFamily="Arial, sans-serif" fontSize="55" fontWeight="bold" fill="white" textAnchor="middle">ॐ</text>
  </svg>
);

const Footer = () => {
  const { language } = useLanguage();
  const content = siteContent[language].footer;
  const nav = siteContent[language].nav;
  const contact = siteContent[language].contact;

  const scrollToSection = (id) => {
    const element = document.querySelector(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-gradient-to-b from-orange-800 to-orange-900 text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
          {/* Logo & Tagline */}
          <div className="sm:col-span-2 text-center sm:text-left">
            <div className="flex items-center gap-3 mb-4 justify-center sm:justify-start">
              <OmLogo className="w-12 h-12 drop-shadow-lg" />
              <div>
                <h3 className="text-xl font-bold">
                  {language === 'gu' ? 'ઉમેદપુરી બાપુ' : 'Umedpuri Bapu'}
                </h3>
                <p className="text-orange-200 text-sm">
                  {language === 'gu' ? 'સાદુથાલા' : 'Saduthala'}
                </p>
              </div>
            </div>
            <p className="text-orange-200 text-base md:text-lg mb-4">{content.tagline}</p>
            <div className="text-xl md:text-2xl font-bold text-amber-400 mb-2">
              "યે દિન ભી ચલેજાયેગે"
            </div>
            <p className="text-orange-200 text-sm italic">
              "This time too shall pass"
            </p>
          </div>

          {/* Quick Links */}
          <div className="text-center sm:text-left">
            <h4 className="text-lg font-bold mb-4 md:mb-6 text-amber-400">
              {language === 'gu' ? 'ત્વરિત લિંક્સ' : 'Quick Links'}
            </h4>
            <ul className="space-y-2 md:space-y-3">
              {['home', 'about', 'teachings', 'bhajans', 'contact'].map((key) => (
                <li key={key}>
                  <button
                    onClick={() => scrollToSection(`#${key}`)}
                    className="text-orange-200 hover:text-amber-400 transition-colors"
                  >
                    {nav[key]}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="text-center sm:text-left">
            <h4 className="text-lg font-bold mb-4 md:mb-6 text-amber-400">
              {language === 'gu' ? 'સંપર્ક' : 'Contact'}
            </h4>
            <ul className="space-y-3 md:space-y-4">
              <li>
                <a
                  href="tel:+919825895999"
                  className="flex items-center gap-3 text-orange-200 hover:text-amber-400 transition-colors justify-center sm:justify-start"
                >
                  <Phone className="w-5 h-5 flex-shrink-0" />
                  {contact.phoneNumber}
                </a>
              </li>
              <li>
                <a
                  href="https://maps.google.com/?q=MG6R+W23,+Navapara,+Saduthala,+Gujarat+384315,+India"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 text-orange-200 hover:text-amber-400 transition-colors justify-center sm:justify-start"
                >
                  <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-left">
                    {siteContent[language].ashram.address}
                  </span>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-orange-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-3 md:gap-4">
            <p className="text-orange-300 text-xs sm:text-sm text-center md:text-left">
              © {new Date().getFullYear()} {language === 'gu' ? 'ઉમેદપુરી બાપુ સાદુથાલા' : 'Umedpuri Bapu Saduthala'}. {content.rights}
            </p>
            <p className="flex items-center gap-2 text-orange-300 text-xs sm:text-sm text-center">
              <Heart className="w-4 h-4 text-red-400" />
              {content.madeBy}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
